import {
    KeyEventType,
    KeyHolder,
    KeyKind,
    KeyState,
    KeyStatus,
    KeyType,
    KeyTypeAttachment,
    KeyTypeAttachmentData,
    KEY_STATUSES,
    RecordableKeyEventType,
    RegisterKey,
    RegisterKeyData,
} from '../types/keyRegister';
import { DATE_ONLY_RE } from './leaseUtils';

/**
 * Pure helpers for the nyckelregister (spec docs/specs/key-register.md):
 * the transition table, overdue, derived counts and holder lookups. The API
 * validates every event through `nextState`; the UI offers `allowedEvents`.
 */

interface Transition {
    from: KeyStatus[];
    /** `null`: the key's state (status, holder, due date) is left as it is. */
    to: KeyStatus | null;
    tagsOnly?: true;
}

/**
 * From → to per event; `created` (— → inStock) is written only with the key.
 * `comment` is the identity transition, allowed in every status (scrapped included).
 */
export const KEY_TRANSITIONS: Record<RecordableKeyEventType, Transition> = {
    handedOut: { from: ['inStock'], to: 'handedOut' },
    returned: { from: ['handedOut'], to: 'inStock' },
    lost: { from: ['inStock', 'handedOut'], to: 'lost' },
    found: { from: ['lost'], to: 'inStock' },
    blocked: { from: ['inStock', 'handedOut', 'lost'], to: 'blocked', tagsOnly: true },
    unblocked: { from: ['blocked'], to: 'inStock', tagsOnly: true },
    scrapped: { from: ['inStock', 'handedOut', 'lost', 'blocked'], to: 'scrapped' },
    comment: { from: KEY_STATUSES, to: null },
};

/** Menu position per event: the comment first, then the status actions. A Record, so a new event type must be placed. */
const EVENT_MENU_POSITION: Record<RecordableKeyEventType, number> = {
    comment: 0,
    handedOut: 1,
    returned: 2,
    lost: 3,
    found: 4,
    blocked: 5,
    unblocked: 6,
    scrapped: 7,
};

/** Every recordable event, in menu order. */
export const EVENT_ORDER: RecordableKeyEventType[] = (Object.keys(EVENT_MENU_POSITION) as RecordableKeyEventType[])
    .sort((a, b) => EVENT_MENU_POSITION[a] - EVENT_MENU_POSITION[b]);

/** Events shown in red (menu item and confirm button): the key leaves use for good or for now. */
export const isDestructiveEvent = (type: RecordableKeyEventType): boolean =>
    type === 'lost' || type === 'blocked' || type === 'scrapped';

/** Only a hand-out and a return can carry a receipt. */
export const eventTakesReceipt = (type: KeyEventType): boolean => type === 'handedOut' || type === 'returned';

/** Hand-out and return can be backdated; every other event happens today. */
export const eventTakesEffectiveDate = (type: KeyEventType): boolean => type === 'handedOut' || type === 'returned';

/**
 * The days a hand-out or return can be dated (YYYY-MM-DD, inclusive): from the effective date of the
 * key's last state-changing event up to today, so the history never runs backwards. A comment does not
 * count, nor does `created`: that is when the key was entered in the register, and a key entered today
 * may have been handed out long before. `min` is undefined for a key without such an event.
 */
export const effectiveDateRange = (key: Pick<RegisterKey, 'log'>, today: string): { min?: string; max: string } => {
    for (let i = key.log.length - 1; i >= 0; i -= 1) {
        const { type, effectiveDate } = key.log[i];
        if (type !== 'comment' && type !== 'created') return { min: effectiveDate, max: today };
    }
    return { max: today };
};

export const isImageFile = (file: Pick<File, 'type'>): boolean => file.type.startsWith('image/');

const isPdfFile = (file: Pick<File, 'type' | 'name'>): boolean =>
    file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

/** A receipt is an image or a PDF scan. */
export const isAcceptedReceiptFile = (file: Pick<File, 'type' | 'name'>): boolean => isImageFile(file) || isPdfFile(file);

/** Files a key type takes: documents and photos, as on lease contracts. */
export const KEY_TYPE_ATTACHMENT_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png', '.gif', '.heic'];

export const isAcceptedTypeAttachmentFile = (file: Pick<File, 'name'>): boolean =>
    KEY_TYPE_ATTACHMENT_EXTENSIONS.includes('.' + (file.name.split('.').pop() ?? '').toLowerCase());

/** Which object collection a link points at: `units` (lägenhet, lokal) or `storageUnits` (förråd). */
export type KeyObjectKind = 'unit' | 'storageUnit';

export interface KeyObjectLink {
    kind: KeyObjectKind;
    objectKey: string;
    buildingKey: string;
}

type ObjectLinkFields = Pick<KeyType, 'opensUnits' | 'opensStorageUnits'>;

/** The objects a type opens, units first. */
export const typeObjectLinks = (type: ObjectLinkFields): KeyObjectLink[] => [
    ...Object.entries(type.opensUnits ?? {}).map(([objectKey, buildingKey]) => ({ kind: 'unit' as const, objectKey, buildingKey })),
    ...Object.entries(type.opensStorageUnits ?? {}).map(([objectKey, buildingKey]) => ({ kind: 'storageUnit' as const, objectKey, buildingKey })),
];

/** Linked directly to this lägenhet, lokal or förråd. */
export const opensObject = (type: ObjectLinkFields, objectKey: string): boolean =>
    !!type.opensUnits?.[objectKey] || !!type.opensStorageUnits?.[objectKey];

/** Opens the building as a whole, or one of its objects. */
export const opensInBuilding = (type: ObjectLinkFields & Pick<KeyType, 'opensBuildings'>, buildingKey: string): boolean =>
    !!type.opensBuildings?.[buildingKey] || typeObjectLinks(type).some(link => link.buildingKey === buildingKey);

/** Files keyed by their node key (a type's attachments, the Låsschema files) as a list, newest first. */
export const attachmentsNewestFirst = (files: { [key: string]: KeyTypeAttachmentData } | undefined): KeyTypeAttachment[] =>
    Object.entries(files ?? {})
        .map(([key, data]) => ({ key, ...data }))
        .sort((a, b) => b.date.localeCompare(a.date) || a.fileName.localeCompare(b.fileName, 'sv'));

/** A type's files, newest first. */
export const typeAttachments = (type: Pick<KeyType, 'attachments'>): KeyTypeAttachment[] => attachmentsNewestFirst(type.attachments);

export const isEventAllowed = (key: Pick<RegisterKeyData, 'status'>, kind: KeyKind, type: KeyEventType): boolean => {
    if (type === 'created') return false;
    const transition = KEY_TRANSITIONS[type];
    if (!transition) return false;
    if (transition.tagsOnly && kind !== 'tag') return false;
    return transition.from.includes(key.status);
};

/** The events that can be recorded on the key now, in a stable menu order. */
export const allowedEvents = (key: Pick<RegisterKeyData, 'status'>, kind: KeyKind): RecordableKeyEventType[] =>
    EVENT_ORDER.filter(type => isEventAllowed(key, kind, type));

export interface KeyEventRequest {
    type: RecordableKeyEventType;
    holder?: KeyHolder;
    dueDate?: string;
}

/**
 * The key's state after the event, or a Swedish error. A hand-out requires a
 * holder and may carry a due date; every other event clears holder and due
 * date (a returned/lost/blocked/scrapped key has no holder); a comment leaves
 * the state unchanged.
 */
export const nextState = (key: KeyState, kind: KeyKind, event: KeyEventRequest): KeyState => {
    const transition = KEY_TRANSITIONS[event.type];
    if (!transition) throw new Error('Okänd händelse');
    if (transition.tagsOnly && kind !== 'tag') throw new Error('Endast taggar kan spärras');
    if (!transition.from.includes(key.status)) throw new Error('Händelsen är inte tillåten för nyckelns nuvarande status');
    if (event.type === 'handedOut') {
        if (!event.holder || !event.holder.key) throw new Error('Mottagare krävs');
        if (event.dueDate !== undefined && !DATE_ONLY_RE.test(event.dueDate)) throw new Error('Ogiltigt återlämningsdatum');
        return event.dueDate ? { status: 'handedOut', holder: event.holder, dueDate: event.dueDate } : { status: 'handedOut', holder: event.holder };
    }
    if (transition.to === null) {
        const same: KeyState = { status: key.status };
        if (key.holder) same.holder = key.holder;
        if (key.dueDate) same.dueDate = key.dueDate;
        return same;
    }
    return { status: transition.to };
};

/** Out with a return date that has passed. `today` is YYYY-MM-DD (todayStockholm). */
export const isOverdue = (key: Pick<RegisterKeyData, 'status' | 'dueDate'>, today: string): boolean =>
    key.status === 'handedOut' && typeof key.dueDate === 'string' && key.dueDate < today;

export type KeyStatusCounts = Record<KeyStatus, number>;

const emptyCounts = (): KeyStatusCounts =>
    Object.fromEntries(KEY_STATUSES.map(status => [status, 0])) as KeyStatusCounts;

export const countByStatus = (keys: Array<Pick<RegisterKeyData, 'status'>>): KeyStatusCounts => {
    const counts = emptyCounts();
    for (const key of keys) counts[key.status] += 1;
    return counts;
};

/** Highest all-digit serial + 1, or 1 when none is numeric. Pass the keys of one type. */
export const nextSerialStart = (keys: Array<Pick<RegisterKeyData, 'serial'>>): number => {
    let highest = 0;
    for (const key of keys) {
        const serial = key.serial?.trim();
        if (serial && /^\d+$/.test(serial)) highest = Math.max(highest, parseInt(serial, 10));
    }
    return highest + 1;
};

export const sameHolder = (a: KeyHolder | undefined, b: KeyHolder): boolean =>
    !!a && a.kind === b.kind && a.key === b.key;

/** Keys the holder has right now (status handedOut). */
export const keysHeldBy = <K extends Pick<RegisterKeyData, 'status' | 'holder'>>(keys: K[], holder: KeyHolder): K[] =>
    keys.filter(key => key.status === 'handedOut' && sameHolder(key.holder, holder));

/** Whether the holder is the current holder or appears in any event of the key. */
export const keyReferencesHolder = (key: Pick<RegisterKey, 'holder' | 'log'>, holder: KeyHolder): boolean =>
    sameHolder(key.holder, holder) || key.log.some(event => sameHolder(event.holder, holder));

const naturalCompare = (a: string, b: string): number => a.localeCompare(b, 'sv', { numeric: true });

/** By serial (natural order, "2" before "10"), keys without serial last, then by key. */
export const sortKeysBySerial = <K extends Pick<RegisterKey, 'serial' | 'key'>>(keys: K[]): K[] =>
    [...keys].sort((a, b) => {
        if (a.serial && b.serial) return naturalCompare(a.serial, b.serial) || (a.key < b.key ? -1 : 1);
        if (a.serial) return -1;
        if (b.serial) return 1;
        return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
    });

/** `effectiveDate` of the latest `handedOut` event while the key is handed out, else undefined. */
export const handedOutSince = (key: Pick<RegisterKey, 'status' | 'log'>): string | undefined => {
    if (key.status !== 'handedOut') return undefined;
    for (let i = key.log.length - 1; i >= 0; i -= 1) {
        if (key.log[i].type === 'handedOut') return key.log[i].effectiveDate;
    }
    return undefined;
};
